import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffLoginPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Please enter the password");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/staff/login`, { password });
      // Store login state
      sessionStorage.setItem("staffLoggedIn", "true");
      toast.success("Welcome back!");
      navigate("/staff/dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex flex-col">
      {/* Header */}
      <header className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-black hover:text-[#A61B1B] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Store</span>
          </Link>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white border-4 border-black p-8 ticket-shadow" data-testid="staff-login-form">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-black text-white rounded-sm flex items-center justify-center">
                <Lock className="w-10 h-10" />
              </div>
            </div>
            
            <h1 className="text-3xl font-[800] text-black text-center mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
              Staff Access
            </h1>
            <p className="text-[#8A8A8A] text-center mb-8">
              Enter password to access the order dashboard
            </p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-sm font-bold text-black uppercase tracking-wide">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter staff password"
                    className="border-2 border-black focus:border-[#A61B1B] rounded-sm p-4 text-lg pr-12"
                    data-testid="staff-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-black"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-black hover:bg-[#333] text-white rounded-sm py-6 text-lg font-bold uppercase"
                data-testid="staff-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  "Access Dashboard"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLoginPage;
